import React, { useState } from 'react';
import { applicationAPI } from '../api/recruitmentApi';
import { jobApplicationsAPI } from '../../services/api';

const ScoringPanel = ({ application, onClose, onUpdate, useOldAPI = false }) => {
  const [scores, setScores] = useState({
    technicalScore: application.technicalScore || 0,
    communicationScore: application.communicationScore || 0,
    experienceScore: application.experienceScore || 0,
    cultureFitScore: application.cultureFitScore || 0
  });
  const [saving, setSaving] = useState(false);

  const handleScoreChange = (key, value) => {
    const numValue = parseFloat(value);
    if (numValue >= 0 && numValue <= 5) {
      setScores(prev => ({ ...prev, [key]: numValue }));
    }
  };

  const calculateOverallScore = () => {
    const weights = {
      technical: 0.3,
      communication: 0.2,
      experience: 0.3,
      cultureFit: 0.2
    };
    
    return (
      Number(scores.technicalScore || 0) * weights.technical +
      Number(scores.communicationScore || 0) * weights.communication +
      Number(scores.experienceScore || 0) * weights.experience +
      Number(scores.cultureFitScore || 0) * weights.cultureFit
    ).toFixed(1);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (useOldAPI) {
        await jobApplicationsAPI.updateScores(application.id, scores);
      } else {
        await applicationAPI.updateScores(application.id, scores);
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update scores:', error);
      alert('Failed to update scores');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Update Candidate Scores</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* Technical Score */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-700">
                Technical Skills
              </label>
              <span className="text-2xl font-bold text-blue-600">
                {Number(scores.technicalScore || 0).toFixed(1)}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={scores.technicalScore}
              onChange={(e) => handleScoreChange('technicalScore', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Programming skills, technical knowledge, problem-solving ability
            </p>
          </div>

          {/* Communication Score */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-700">
                Communication
              </label>
              <span className="text-2xl font-bold text-green-600">
                {Number(scores.communicationScore || 0).toFixed(1)}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={scores.communicationScore}
              onChange={(e) => handleScoreChange('communicationScore', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Written and verbal communication, presentation skills
            </p>
          </div>

          {/* Experience Score */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-700">
                Experience
              </label>
              <span className="text-2xl font-bold text-purple-600">
                {Number(scores.experienceScore || 0).toFixed(1)}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={scores.experienceScore}
              onChange={(e) => handleScoreChange('experienceScore', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Relevant work experience, project portfolio, achievements
            </p>
          </div>

          {/* Culture Fit Score */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-700">
                Culture Fit
              </label>
              <span className="text-2xl font-bold text-pink-600">
                {Number(scores.cultureFitScore || 0).toFixed(1)}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={scores.cultureFitScore}
              onChange={(e) => handleScoreChange('cultureFitScore', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Values alignment, team compatibility, motivation
            </p>
          </div>

          {/* Overall Score */}
          <div className="pt-6 border-t">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-700">Overall Score</p>
                <p className="text-sm text-gray-500">Weighted average (Tech: 30%, Comm: 20%, Exp: 30%, Culture: 20%)</p>
              </div>
              <div className="text-4xl font-bold text-blue-600">
                {calculateOverallScore()}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Scores'}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringPanel;
